import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CaretDown, CaretRight, CheckCircle, Warning, XCircle } from "@phosphor-icons/react";
import { RSpin, RSpinBlock } from "@/components/RSpin";
import DateRangePresets from "@/components/DateRangePresets";

/**
 * Daily Reports Approval — date-grouped accordion of conversions awaiting
 * admin approval. Each day expands to show per-brand rollup + per-action
 * detail with checkboxes for bulk approve/revoke. Pure presentational —
 * all callbacks + state come from the parent.
 */
export function DailyReportsApproval({
  dailyReports,
  loadingReports,
  syncing,
  totalApproved,
  totalDays,
  autoApproveEnabled = true,
  dateRange,
  onDateRangeChange,
  expandedDate,
  setExpandedDate,
  dayItemsCache = {},
  dayLoading = null,
  selectedActionIds,
  setSelectedActionIds,
  onSync,
  onRefresh,
  onRevokeDay,
  onRevokeActions,
}) {
  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">Daily Report Approval</h3>
          <p className="text-xs text-slate-500 mt-1">
            {loadingReports
              ? "Loading…"
              : autoApproveEnabled
                ? `${totalApproved} of ${totalDays} days approved · Actions auto-approve on sync (network state may still show PENDING)`
                : `${totalApproved} of ${totalDays} days approved · Approve each day to make it visible to the publisher`}
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          {dateRange && onDateRangeChange && (
            <DateRangePresets
              preset={dateRange.preset}
              start={dateRange.start}
              end={dateRange.end}
              onChange={onDateRangeChange}
              testId="reports-approval-date-range"
            />
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={onSync}
              disabled={syncing || loadingReports}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="sync-impact-btn"
            >
              {syncing ? "Syncing…" : "Sync from Impact"}
            </Button>
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loadingReports} data-testid="refresh-daily-reports">Refresh</Button>
          </div>
        </div>
      </div>

      {loadingReports ? (
        <RSpinBlock label="Loading daily reports" height={320} data-testid="daily-reports-spinner" />
      ) : dailyReports.length === 0 ? (
        <div className="border border-slate-200 rounded-md p-12 text-center">
          <p className="text-sm text-slate-500 mb-3">
            {dateRange?.start && dateRange?.end
              ? "No action data in this date range."
              : "No action data yet for this publisher."}
          </p>
          <p className="text-xs text-slate-400 mb-5">
            {dateRange?.start && dateRange?.end
              ? "Try widening the date filter, or sync the latest conversions from Impact.com."
              : "Click below to pull the latest conversions from Impact.com."}
          </p>
          <Button
            onClick={onSync}
            disabled={syncing}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="sync-impact-empty-btn"
          >
            {syncing ? "Syncing…" : "Sync Actions from Impact"}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {dailyReports.map(day => {
            const isExpanded = expandedDate === day.date;
            const approvedCount = day.approved_actions ?? 0;
            // REVERSED never enters action_approvals and must not look
            // like "pending approval". Prefer backend pending_actions;
            // fall back for older payloads.
            const pendingCount = day.pending_actions ?? Math.max(
              0,
              (day.total_actions || 0) - approvedCount - (day.reversed_actions || 0)
            );
            const reversedCount = day.reversed_actions || 0;
            // total / approved — total includes REVERSED
            // (total = approved + pending + reversed).
            const totalCount = day.total_actions || 0;
            const totalEarning = day.brands.reduce((s, b) => s + b.earning, 0);
            const approvedEarning = day.brands.reduce((s, b) => s + (b.approved_earning || 0), 0);
            return (
              <div key={day.date} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`daily-row-${day.date}`}>
                {/* Date Header Row */}
                <div className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${day.all_approved ? "bg-emerald-50/50" : "bg-white hover:bg-slate-50"}`} onClick={() => setExpandedDate(isExpanded ? null : day.date)}>
                  <div className="flex items-center gap-3">
                    {isExpanded ? <CaretDown size={14} className="text-slate-400" /> : <CaretRight size={14} className="text-slate-400" />}
                    <span className="font-mono text-sm font-semibold text-slate-900">{day.date}</span>
                    <span className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-900">{totalCount}</span>
                      <span className="mx-0.5">/</span>
                      <span className="font-semibold text-emerald-600">{approvedCount}</span> approved
                      {pendingCount > 0 && <span className="ml-1 text-amber-600 font-semibold">· {pendingCount} pending</span>}
                      {reversedCount > 0 && <span className="ml-1 text-slate-400">· {reversedCount} reversed</span>}
                    </span>
                    <span className="text-xs font-mono text-slate-500">${day.total_amount.toFixed(2)}</span>
                    <span className="text-xs font-mono text-[#002FA7] font-semibold">
                      ${approvedEarning.toFixed(2)}
                      {pendingCount > 0 && <span className="text-slate-400 font-normal"> / ${totalEarning.toFixed(2)}</span>}
                    </span>
                    {(day.skimlinks_unpaid_count || 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200"
                        title={`${day.skimlinks_unpaid_count} Skimlinks sale${day.skimlinks_unpaid_count === 1 ? "" : "s"} not yet paid by Skimlinks. Approving now commits you to paying the publisher with money you haven't received.`}
                        data-testid={`day-skim-unpaid-${day.date}`}
                      >
                        <Warning size={10} weight="fill" />
                        {day.skimlinks_unpaid_count} unpaid
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {day.all_approved ? (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700"><CheckCircle size={12} weight="fill" /> All approved</span>
                        {approvedCount > 0 && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); onRevokeDay(day.date); }} data-testid={`revoke-${day.date}`}>Revoke all</Button>
                        )}
                      </div>
                    ) : pendingCount > 0 ? (
                      // Auto-approve normally clears pending on next sync.
                      // Still show a manual approve button as a fallback
                      // for legacy pubs opted out of auto-approve.
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600" title="Pending — will auto-approve on next sync">
                        {pendingCount} pending
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* Expanded Brand Details */}
                {isExpanded && (
                  dayLoading === day.date ? (
                    <div className="border-t border-slate-200 py-6 flex items-center justify-center" data-testid={`day-items-loading-${day.date}`}>
                      <RSpin size="md" label="Loading day details" />
                    </div>
                  ) : (
                    <DayBrandsTable
                      day={day}
                      itemsByBrand={dayItemsCache[day.date] || {}}
                      selectedActionIds={selectedActionIds}
                      setSelectedActionIds={setSelectedActionIds}
                      onRevokeDay={onRevokeDay}
                      onRevokeActions={onRevokeActions}
                    />
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function DayBrandsTable({ day, itemsByBrand = {}, selectedActionIds, setSelectedActionIds, onRevokeDay, onRevokeActions }) {
  // Merge the lazy-loaded items array onto each brand rollup. The
  // rollup row carries the totals (no items[]); itemsByBrand[brand_name]
  // carries the per-action drill-down loaded on day expand.
  const brandsWithItems = (day.brands || []).map((b) => ({
    ...b,
    items: b.items && b.items.length ? b.items : (itemsByBrand[b.brand_name] || []),
  }));
  return (
    <div className="border-t border-slate-200 overflow-x-auto" data-testid={`day-brands-scroll-${day.date}`}>
      <table className="w-full min-w-[1040px] text-sm">
        <thead><tr className="bg-slate-50 border-b border-slate-100">
          <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Actions</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Amount</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Network Paid</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Network %</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Pub Commission</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Pub Earning</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Pub %</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Margin</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Margin %</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
        </tr></thead>
        <tbody>
          {brandsWithItems.map(b => {
            const brandKey = `${day.date}|${b.brand_name}`;
            // Prefer live items when loaded; otherwise trust rollup pending_actions
            // (REVERSED are already excluded from that count on the backend).
            const brandPendingCount = (b.items || []).length
              ? (b.items || []).filter(it => !it.approved && it.state !== "REVERSED").length
              : (b.pending_actions || 0);
            const brandMargin = +(b.payout - b.earning).toFixed(2);
            const pct = (num, denom) => denom > 0 ? (num / denom) * 100 : null;
            const brandImpactPct = pct(b.payout, b.amount);
            const pubCommission = Number(b.pub_commission ?? 0);
            // Pub % = realised commission/sale (shows network % when network
            // cleared less than the assigned pub rate, e.g. 5.5% vs 7%).
            const brandPubPct = pct(pubCommission, b.amount);
            // Margin % of network paid (platform take of what Impact paid us).
            const brandMarginPct = pct(brandMargin, b.payout);
            const fmtPct = (v) => v == null || Number.isNaN(v) ? "—" : `${Number(v).toFixed(2)}%`;
            const assignedPubPct = (b.commission_pct != null && b.commission_pct !== "")
              ? Number(b.commission_pct)
              : null;
            return [
              <tr
                key={`${brandKey}-row`}
                className="border-b border-slate-100 bg-slate-50/40"
                data-testid={`brand-row-${day.date}-${b.brand_name}`}
              >
                <td className="px-4 py-2.5 font-medium text-slate-900">
                  <span className="inline-flex items-center gap-1.5">
                    {(b.skimlinks_unpaid_count || 0) > 0 && (
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-amber-500 shrink-0"
                        title={`${b.skimlinks_unpaid_count} of ${b.actions} sale${b.actions === 1 ? "" : "s"} ($${(b.skimlinks_unpaid_earning || 0).toFixed(2)} pub earning) not yet paid by Skimlinks. Approve only if you're OK fronting the cash.`}
                        data-testid={`brand-skim-unpaid-dot-${day.date}-${b.brand_name}`}
                      />
                    )}
                    {b.brand_name}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  <span className="font-semibold text-slate-900">{b.actions}</span>
                  <span className="text-slate-400">/</span>
                  <span className="text-emerald-600 font-semibold">{b.approved_actions}</span>
                  {(b.reversed_actions || 0) > 0 && (
                    <span className="block text-[10px] text-slate-400 font-normal">{b.reversed_actions} reversed</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">${b.amount.toFixed(2)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-slate-500">${b.payout.toFixed(2)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-slate-500">{fmtPct(brandImpactPct)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-slate-700">${pubCommission.toFixed(2)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-semibold text-[#002FA7]">
                  ${b.approved_earning.toFixed(2)}
                  {b.pending_earning > 0 && <span className="text-amber-600 font-normal"> / <span className="text-slate-400">${b.earning.toFixed(2)}</span></span>}
                </td>
                <td
                  className="px-4 py-2.5 text-right font-mono text-[#002FA7]"
                  title={assignedPubPct != null ? `Assigned pub rate: ${assignedPubPct}%` : undefined}
                >
                  {fmtPct(brandPubPct)}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono ${brandMargin >= 0 ? "text-emerald-600" : "text-red-500"}`}>${brandMargin.toFixed(2)}</td>
                <td className={`px-4 py-2.5 text-right font-mono ${brandMargin >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmtPct(brandMarginPct)}</td>
                <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                  {b.approved ? (
                    <div className="flex items-center justify-end gap-1">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700"><CheckCircle size={10} weight="fill" /> All approved</span>
                      {(b.approved_actions || 0) > 0 && (
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-400 hover:text-red-600" onClick={() => onRevokeDay(day.date, b.brand_name)}>
                          <XCircle size={12} />
                        </Button>
                      )}
                    </div>
                  ) : brandPendingCount > 0 ? (
                    // Auto-approve clears pending on next sync; show a quiet indicator only.
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600" title="Will auto-approve on next sync">
                      {brandPendingCount} pending
                    </span>
                  ) : null}
                </td>
              </tr>,
              (b.items || []).length > 0 ? (
                <BrandActionsRow
                  key={`${brandKey}-items`}
                  brand={b}
                  brandKey={brandKey}
                  selectedActionIds={selectedActionIds}
                  setSelectedActionIds={setSelectedActionIds}
                  onRevokeActions={onRevokeActions}
                />
              ) : null,
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

function BrandActionsRow({ brand, brandKey, selectedActionIds, setSelectedActionIds, onRevokeActions }) {
  return (
    <tr className="bg-white">
      {/* max-w-0 forces this cell to respect the parent table width so
          overflow-x-auto below can scroll instead of expanding forever. */}
      <td colSpan={11} className="px-0 py-0 max-w-0">
        <div className="px-4 sm:px-8 py-3">
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
              Per-action breakdown · {brand.items.length} sale{brand.items.length === 1 ? "" : "s"}
            </div>
            {(() => {
              const selInBrand = brand.items.filter(it => selectedActionIds.has(it.action_id));
              if (selInBrand.length === 0) return null;
              const selPending = selInBrand.filter(it => !it.approved).map(it => it.action_id);
              const selApproved = selInBrand.filter(it => it.approved).map(it => it.action_id);
              return (
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-slate-500">{selInBrand.length} selected</span>
                  {selApproved.length > 0 && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-500 hover:text-red-700 border-red-200" onClick={() => onRevokeActions(selApproved, `${selApproved.length} selected`)}>
                      Revoke {selApproved.length}
                    </Button>
                  )}
                  {selPending.length > 0 && (
                    <span className="text-[10px] text-slate-400">({selPending.length} pending, auto-approve on next sync)</span>
                  )}
                </div>
              );
            })()}
          </div>
          {/* Nested scroll so State / Approval stay reachable when Action ID is long */}
          <div className="overflow-x-auto w-full" data-testid={`items-scroll-${brandKey}`}>
            <table className="w-full min-w-[1100px] text-[11px]" data-testid={`items-table-${brandKey}`}>
              <thead>
                <tr className="text-slate-400 border-b border-slate-200">
                  <th className="text-left px-2 py-1.5 w-7">
                    <Checkbox
                      checked={brand.items.every(it => selectedActionIds.has(it.action_id))}
                      onCheckedChange={(v) => {
                        setSelectedActionIds(prev => {
                          const next = new Set(prev);
                          brand.items.forEach(it => {
                            if (v) next.add(it.action_id);
                            else next.delete(it.action_id);
                          });
                          return next;
                        });
                      }}
                    />
                  </th>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Time</th>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Action ID</th>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Order / Promo</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Amount</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Network Paid</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Network %</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Pub Commission</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Pub Earning</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Pub %</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Margin</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Margin %</th>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">State</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wider whitespace-nowrap">Approval</th>
                </tr>
              </thead>
              <tbody>
                {brand.items.map((it, idx) => {
                  const amt = it.amount || 0;
                  const paid = it.payout || 0;
                  const commission = it.pub_commission != null
                    ? Number(it.pub_commission)
                    : Math.min(
                      amt * (Number(it.commission_pct ?? brand.commission_pct) || 0) / 100,
                      paid,
                    );
                  const earn = it.earning || 0;
                  const margin = +(paid - earn).toFixed(2);
                  const pctOfSale = (num) => amt > 0 ? `${(num / amt * 100).toFixed(2)}%` : "—";
                  const pctOfPaid = (num) => paid > 0 ? `${(num / paid * 100).toFixed(2)}%` : "—";
                  // Realised Pub % = commission/sale (capped by network %).
                  const assignedPct = Number(it.commission_pct ?? brand.commission_pct);
                  const pubPct = amt > 0
                    ? pctOfSale(commission)
                    : (Number.isFinite(assignedPct) ? `${assignedPct.toFixed(2)}%` : "—");
                  const pubPctTitle = Number.isFinite(assignedPct)
                    ? `Assigned pub rate: ${assignedPct}%`
                    : undefined;
                  const stateClass =
                    it.state === "APPROVED" ? "text-emerald-600" :
                    it.state === "REVERSED" ? "text-red-500" :
                    "text-amber-600";
                  const rowChecked = selectedActionIds.has(it.action_id);
                  const actionId = it.action_id || "";
                  return (
                    <tr key={it.action_id || idx} className={`border-b border-slate-100 ${it.approved ? "bg-emerald-50/30" : ""}`}>
                      <td className="px-2 py-1.5">
                        <Checkbox
                          checked={rowChecked}
                          onCheckedChange={(v) => {
                            setSelectedActionIds(prev => {
                              const next = new Set(prev);
                              if (v) next.add(it.action_id); else next.delete(it.action_id);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td className="px-2 py-1.5 font-mono text-slate-500 whitespace-nowrap">{(it.event_date || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-700 max-w-[140px]">
                        <span className="block truncate" title={actionId}>{actionId}</span>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-slate-500 max-w-[120px]">
                        <span className="block truncate" title={it.order_id || it.promo_code || ""}>
                          {it.order_id || it.promo_code || "-"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-900 whitespace-nowrap" data-testid={`item-amount-${it.action_id}`}>
                        ${amt.toFixed(2)}
                        {it.currency && it.currency !== "USD" && it.amount_native != null && (
                          <span
                            className="block text-[9px] text-slate-400 font-normal"
                            title={`Native currency: ${it.currency}`}
                          >
                            {it.currency} {(it.amount_native || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500 whitespace-nowrap">${paid.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500 whitespace-nowrap">{pctOfSale(paid)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-700 whitespace-nowrap">${Number(commission || 0).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-semibold text-[#002FA7] whitespace-nowrap">${earn.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-[#002FA7] whitespace-nowrap" title={pubPctTitle}>{pubPct}</td>
                      <td className={`px-2 py-1.5 text-right font-mono whitespace-nowrap ${margin >= 0 ? "text-emerald-600" : "text-red-500"}`}>${margin.toFixed(2)}</td>
                      <td className={`px-2 py-1.5 text-right font-mono whitespace-nowrap ${margin >= 0 ? "text-emerald-600" : "text-red-500"}`}>{pctOfPaid(margin)}</td>
                      <td className={`px-2 py-1.5 font-semibold whitespace-nowrap ${stateClass}`}>
                        <span className="inline-flex items-center gap-1">
                          {(it.skimlinks_payment_status === "pending" ||
                            it.skimlinks_payment_status === "scheduled" ||
                            it.skimlinks_payment_status === "unpaid") && (
                            <span
                              className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"
                              title={`Skimlinks payment status: ${it.skimlinks_payment_status}. They haven't paid us this commission yet — approving commits you to paying out money you haven't received.`}
                              data-testid={`item-skim-unpaid-${it.action_id}`}
                            />
                          )}
                          {it.state || "PENDING"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {it.approved ? (
                          <Button size="sm" variant="ghost" className="h-5 text-[9px] text-red-500 hover:text-red-700" onClick={() => onRevokeActions([it.action_id], it.action_id)} data-testid={`revoke-action-${it.action_id}`}>
                            ✓ Approved · Revoke
                          </Button>
                        ) : it.state === "REVERSED" ? (
                          <span className="text-[10px] text-red-400 italic">Reversed — cannot approve</span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-100 text-slate-500" title="Will auto-approve on next sync">
                            pending
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  );
}
